import {
  DEFAULT,
  SHORT,
  LONG,
  NUMERIC,
  Options,
  VALID_FORMATS,
} from './consts';
import { isValidDate, getValidLocale, isNumber } from './helpers';

/**
 * `displayDate` takes a `Date` object and a format specifier, and returns a string representing date that is formatting according to format for the user's locale.
 * @param date - `Date` object or `number` to display as `string`.
 * @param options - parameters object available for the Intl.DateTimeFormat() constructor:
 *   * `format` - either `"default"`/`"short"` (abbreviated month, e.g. `'Jan 23, 2015'`), `"long"` (full month name, e.g. `'January 23, 2015'`), or `"numeric"` (e.g. `'1/23/2015'`). Defaults to `"short"` when omitted.
 *   * `locale` - developer designated locale (this will take precedence over browser setting, if language differs).
 *   * `month` - overrides the month style derived from `format` (e.g. `'narrow'` for a single-letter month). Use `format: 'long'` for full month names.
 *   * `timeZone` - converts `date` to the given timezone.
 *   * `utc` - converts `date` to UTC timezone if true. Defaults to false.
 * See MDN docs for complete list of options: https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Global_Objects/Intl/DateTimeFormat/DateTimeFormat#options
 * @returns a locale date string corresponding to current user's locale and format options.
 */
export const displayDate = (
  date: Date | number,
  options: Options = {},
): string => {
  if (!isValidDate(date)) {
    throw new TypeError(
      `The first argument must be a valid date but was "${date}".`,
    );
  }

  const { format, locale, utc, timeZone, weekday, year, month } = options;

  if (!VALID_FORMATS.has(format)) {
    const supported = Array.from(VALID_FORMATS)
      .filter((f) => !!f)
      .map((f) => JSON.stringify(f));
    supported[supported.length - 1] = `or ${supported[supported.length - 1]}`;
    throw new TypeError(
      `The format must be either: ${supported.join(', ')} but was "${format}".`,
    );
  }

  const _options: Intl.DateTimeFormatOptions = {};
  _options.day = NUMERIC;
  _options.year = NUMERIC;

  switch (format) {
    case NUMERIC:
      _options.month = NUMERIC;
      break;
    case LONG:
      _options.month = 'long';
      if (weekday) {
        _options.weekday = SHORT;
      }
      if (year === false) {
        delete _options.year;
      }
      break;
    case SHORT:
    case DEFAULT:
    default:
      _options.month = SHORT;
      if (weekday) {
        _options.weekday = SHORT;
      }
      if (year === false) {
        delete _options.year;
      }
      break;
  }

  if (month) {
    _options.month = month;
  }

  _options.timeZone = timeZone || (utc ? 'UTC' : undefined);

  const localeId = getValidLocale(locale);
  const finalDate = new Date(isNumber(date) ? date : date.getTime());

  return finalDate.toLocaleDateString(localeId, _options);
};
